import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import PageTitle from "../components/PageTitle";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Stack>
  );
}

function P({ children }: { children: ReactNode }) {
  const theme = useTheme();
  return (
    <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
      {children}
    </Typography>
  );
}

function Bullets({ items }: { items: ReactNode[] }) {
  const theme = useTheme();
  return (
    <Box component="ul" sx={{ pl: 3, m: 0 }}>
      {items.map((item, i) => (
        <Typography
          key={i}
          component="li"
          variant="body1"
          sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
}

export default function Privacy() {
  const theme = useTheme();
  const contactEmail = "jbreckman@prettychitty.com";

  return (
    <Box>
      <PageTitle title="Privacy Policy" subTitle="Effective Date: April 28, 2026" />

      <Box sx={{ maxWidth: 760 }}>
        <Stack spacing={4}>
          <P>
            This Privacy Policy describes how Pretty Chitty, LLC ("Pretty Chitty,"
            "we," "us," or "our") collects, uses, and shares information when you
            use our websites and services, including the Pretty Chitty and Really
            Chitty platforms (collectively, the "Service").
          </P>

          <Section title="1. Information We Collect">
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              Information you provide
            </Typography>
            <Bullets
              items={[
                <>
                  <strong>Discord account information.</strong> When you sign in
                  with Discord, we receive and store your Discord user ID,
                  username, and avatar URL.{" "}
                  <strong>We do not receive or store your email address.</strong>
                </>,
                <>
                  <strong>Game and match content.</strong> Information about the
                  games you play and create — match history, in-game actions, and
                  any text or content you submit through the Service. On Really
                  Chitty, this includes the metadata and links to game code that
                  you publish.
                </>,
                <>
                  <strong>Communications.</strong> If you contact us through our
                  contact form, we receive the name, email, and message you submit.
                </>,
              ]}
            />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, color: theme.palette.text.primary, mt: 1 }}
            >
              Information collected automatically
            </Typography>
            <Bullets
              items={[
                <>
                  <strong>Usage data.</strong> When you use the Service, we
                  automatically collect information such as your IP address,
                  browser type, device information, pages viewed, and interactions
                  with the Service.
                </>,
                <>
                  <strong>Session replay and analytics.</strong> We use FullStory
                  to record and analyze how visitors interact with the Service,
                  including mouse movements, clicks, scrolling, and pages visited.
                </>,
                <>
                  <strong>Cookies and similar technologies.</strong> We use
                  cookies for authentication (to keep you signed in) and through
                  FullStory for analytics. You can control cookies through your
                  browser settings.
                </>,
              ]}
            />
          </Section>

          <Section title="2. How We Use Information">
            <P>We use the information we collect to:</P>
            <Bullets
              items={[
                "Provide, maintain, and improve the Service;",
                "Authenticate you and manage your account;",
                "Match you with other players and run games;",
                "Send game-related notifications through Discord — we do not send marketing emails;",
                "Detect, prevent, and address fraud, abuse, security issues, and technical problems;",
                "Comply with legal obligations.",
              ]}
            />
          </Section>

          <Section title="3. How We Share Information">
            <P>
              We do not sell your personal information. We share information only
              as follows:
            </P>
            <Bullets
              items={[
                <>
                  <strong>Discord.</strong> We integrate with Discord for
                  authentication and to send game-related notifications via our
                  Discord bot. Your interactions with Discord are governed by
                  Discord's privacy policy.
                </>,
                <>
                  <strong>FullStory.</strong> We share usage and session data with
                  FullStory for analytics and session replay.
                </>,
                <>
                  <strong>Formspree.</strong> Messages you submit through our
                  contact form are processed by Formspree.
                </>,
                <>
                  <strong>Hosting and infrastructure providers.</strong> We use
                  third-party providers to host the Service and store data on
                  our behalf.
                </>,
                <>
                  <strong>Legal and safety.</strong> We may disclose information
                  when required by law, to enforce our Terms, or to protect the
                  rights, property, or safety of Pretty Chitty, our users, or
                  others.
                </>,
                <>
                  <strong>Business transfers.</strong> If we are involved in a
                  merger, acquisition, or sale of assets, your information may be
                  transferred as part of that transaction.
                </>,
              ]}
            />
          </Section>

          <Section title="4. Your Rights">
            <P>
              You may request to access, export, or delete the personal
              information we hold about you by contacting us at {contactEmail}.
              We will respond within a reasonable time. Depending on your
              location, you may have additional rights under laws such as the
              GDPR or CCPA, including the right to object to or restrict
              processing.
            </P>
          </Section>

          <Section title="5. Children">
            <P>
              The Service is not intended for children under 13. We do not
              knowingly collect personal information from anyone under 13. If you
              believe a child under 13 has provided us with personal information,
              please contact us and we will delete it.
            </P>
          </Section>

          <Section title="6. Data Retention">
            <P>
              We retain your information for as long as your account is active or
              as needed to provide the Service. You may request deletion at any
              time by contacting us, subject to limited exceptions for legal,
              security, or technical reasons.
            </P>
          </Section>

          <Section title="7. Security">
            <P>
              We use reasonable technical and organizational measures to protect
              your information. No method of transmission or storage is completely
              secure, however, and we cannot guarantee absolute security.
            </P>
          </Section>

          <Section title="8. International Users">
            <P>
              The Service is operated from the United States. If you access it
              from outside the United States, your information may be transferred
              to, stored, and processed in the United States.
            </P>
          </Section>

          <Section title="9. Changes to This Policy">
            <P>
              We may update this Privacy Policy from time to time. If we make
              material changes, we will update the effective date above and,
              where appropriate, provide additional notice.
            </P>
          </Section>

          <Section title="10. Contact">
            <P>
              Pretty Chitty, LLC
              <br />
              New Jersey, USA
              <br />
              {contactEmail}
            </P>
          </Section>
        </Stack>
      </Box>
    </Box>
  );
}
